import React, { useMemo, useRef, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { usd, num, ago } from "@/lib/format";
import { Panel, StatusBadge, ProvenanceTag, EmptyState } from "@/components/common/primitives";
import {
  adaptOffers, buildOfferSeriesMatrix, buildOfferSeries, buildBuyBoxStats, buildUsedBuyBoxStats,
  OFFER_COLUMNS, OFFER_PRESETS, SELLER_COLORS, FULFILLMENT_LABEL,
} from "@/lib/analyzerData";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend, Brush,
} from "recharts";
import {
  Search, Columns3, ExternalLink, ChevronDown, ChevronRight, ArrowRight, RotateCcw, Store, ShoppingBag, AlertTriangle,
} from "lucide-react";

const DASH = "—";
const NP = "Not provided by the current Walmart data source.";
const fmtDT = (t) => new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const fmtD = (t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const bool = (v, yes = "Yes", no = "No") => (v === undefined ? DASH : v ? yes : no);

const fmtMiniDay = (t) => new Date(t).toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
const fmtMiniHour = (t) => new Date(t).toLocaleTimeString(undefined, { hour: "numeric" });

function Info({ text }) {
  return <span className="ml-1 text-ink-muted cursor-help" title={text} tabIndex={0} aria-label={text}>ⓘ</span>;
}

// Only mounts its children (heavy recharts) once scrolled near the viewport.
function useInView(ref, rootMargin = "300px") {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView || !ref.current || typeof IntersectionObserver === "undefined") { if (typeof IntersectionObserver === "undefined") setInView(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { rootMargin });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, inView, rootMargin]);
  return inView;
}

function MiniTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="rounded-ctrl border border-edge-subtle bg-surface-0 shadow-float px-2 py-1 text-micro">
      <div className="font-mono text-ink-muted">{fmtDT(label)}</div>
      <b className="text-ink-primary tabnum">{v == null ? "No observation" : unit === "$" ? usd(v) : num(v)}</b>
    </div>
  );
}

// In-row readable mini-chart: 220x88px, real axes, step line, exact tooltip, gaps preserved (no interpolation).
const MiniChart = React.memo(function MiniChart({ data, color, unit, hourly, onClick, label, testid }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const has = data.some((d) => d.v != null);
  return (
    <button ref={ref} data-testid={testid} onClick={onClick} title={label} aria-label={label}
      className="block rounded-ctrl border border-edge-subtle bg-surface-0 hover:ring-1 hover:ring-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 transition-shadow"
      style={{ width: 220, height: 88 }}>
      {!has ? (
        <span className="grid place-items-center h-full text-micro text-ink-muted">No history for this period</span>
      ) : !inView ? (
        <span className="grid place-items-center h-full text-micro text-ink-muted">…</span>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" vertical={false} />
            <XAxis dataKey="t" tickFormatter={hourly ? fmtMiniHour : fmtMiniDay} tick={{ fontSize: 8, fill: "#8A94A6" }} minTickGap={26} height={14} tickLine={false} />
            <YAxis width={34} tick={{ fontSize: 8, fill: "#8A94A6" }} tickFormatter={(v) => (unit === "$" ? `$${v}` : num(v))} tickLine={false} axisLine={false} domain={["auto", "auto"]} />
            <RTooltip content={(p) => <MiniTooltip {...p} unit={unit} />} />
            <Line type="step" dataKey="v" stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </button>
  );
});

function GraphTooltip({ active, payload, label, sellers, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-ctrl border border-edge-subtle bg-surface-0 shadow-float px-2.5 py-2 text-micro">
      <div className="font-mono text-ink-muted mb-1">{fmtDT(label)}</div>
      {payload.filter((p) => p.value != null).map((p) => {
        const s = sellers.find((x) => x.sellerId === p.dataKey);
        return (
          <div key={p.dataKey} className="flex items-center gap-1.5 tabnum">
            <span className="h-2 w-2 rounded-full" style={{ background: p.stroke }} />
            <span className="text-ink-secondary">{s?.sellerName} · {s?.sellerId}</span>
            <b className="text-ink-primary ml-auto">{unit === "$" ? usd(p.value) : num(p.value)}</b>
          </div>
        );
      })}
    </div>
  );
}

function FullGraph({ title, testid, points, sellers, selectedId, unit, hidden, onLegendClick, hourly }) {
  const [chartKey, setChartKey] = useState(0);
  const empty = !points.length || sellers.every((s) => points.every((p) => p[s.sellerId] == null));
  return (
    <Panel className="p-3" testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-stitle text-ink-primary">{title}</span>
        <button onClick={() => setChartKey((k) => k + 1)} className="inline-flex items-center gap-1 h-7 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2" data-testid={`${testid}-reset`}>
          <RotateCcw className="h-3 w-3" />Reset zoom
        </button>
      </div>
      {empty ? (
        <div className="h-[220px] grid place-items-center text-compact text-ink-muted">No history for this period.</div>
      ) : (
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer key={chartKey} width="100%" height="100%">
            <LineChart data={points} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" vertical={false} />
              <XAxis dataKey="t" tickFormatter={hourly ? fmtDT : fmtD} minTickGap={48} tick={{ fontSize: 10, fill: "#8A94A6" }} />
              <YAxis width={44} tick={{ fontSize: 10, fill: "#8A94A6" }} tickFormatter={(v) => (unit === "$" ? `$${v}` : v)} />
              <RTooltip content={(p) => <GraphTooltip {...p} sellers={sellers} unit={unit} />} />
              <Legend onClick={(e) => onLegendClick(e.dataKey)} wrapperStyle={{ fontSize: 11, cursor: "pointer" }} />
              {sellers.map((s, i) => (
                <Line key={s.sellerId} type="step" dataKey={s.sellerId} name={s.sellerName} stroke={SELLER_COLORS[i % SELLER_COLORS.length]}
                  strokeWidth={selectedId === s.sellerId ? 2.6 : 1.4}
                  strokeOpacity={hidden.has(s.sellerId) ? 0 : (selectedId && selectedId !== s.sellerId ? 0.22 : 1)}
                  dot={false} isAnimationActive={false} connectNulls={false} />
              ))}
              <Brush dataKey="t" height={16} travellerWidth={8} tickFormatter={fmtD} stroke="#059669" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

// ---- column preferences (persisted) ----
const COLS_KEY = "ez-offer-columns-v1";
function useColumnPrefs() {
  const [visible, setVisible] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(COLS_KEY)); if (Array.isArray(s?.visible) && s.visible.length) return s.visible; } catch { /* ignore */ }
    return OFFER_PRESETS.Overview;
  });
  useEffect(() => { try { localStorage.setItem(COLS_KEY, JSON.stringify({ visible })); } catch { /* ignore */ } }, [visible]);
  const ordered = OFFER_COLUMNS.filter((c) => visible.includes(c.id)).map((c) => c.id);
  return { visible: ordered, setVisible, isOn: (id) => visible.includes(id) };
}

function ColumnMenu({ prefs }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const toggle = (id) => prefs.setVisible((v) => (v.includes(id) ? v.filter((x) => x !== id && x !== undefined) : [...v, id]));
  return (
    <div className="relative">
      <button data-testid="offers-columns-btn" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2 ez-press">
        <Columns3 className="h-3.5 w-3.5" />Columns
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-64 rounded-panel border border-edge-subtle bg-surface-0 shadow-float p-2" data-testid="offers-columns-menu">
          <div className="flex items-center gap-1.5 h-8 px-2 rounded-ctrl border border-edge-subtle mb-2">
            <Search className="h-3.5 w-3.5 text-ink-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search columns" className="bg-transparent text-compact focus:outline-none w-full" />
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {Object.keys(OFFER_PRESETS).map((p) => (
              <button key={p} data-testid={`offers-preset-${p}`} onClick={() => prefs.setVisible(OFFER_PRESETS[p])}
                className="h-6 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-emerald-50 hover:text-emerald-700">{p}</button>
            ))}
          </div>
          <div className="max-h-56 overflow-y-auto ez-scroll">
            {OFFER_COLUMNS.filter((c) => c.label.toLowerCase().includes(q.toLowerCase())).map((c) => (
              <label key={c.id} className="flex items-center gap-2 py-1 text-compact cursor-pointer">
                <input type="checkbox" data-testid={`offers-col-${c.id}`} checked={prefs.isOn(c.id)} disabled={c.always} onChange={() => toggle(c.id)} className="h-3.5 w-3.5" />
                <span className={cn(c.always && "text-ink-muted")}>{c.label}</span>
              </label>
            ))}
          </div>
          <button data-testid="offers-col-reset" onClick={() => prefs.setVisible(OFFER_PRESETS.Overview)} className="mt-2 inline-flex items-center gap-1 h-7 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2">
            <RotateCcw className="h-3 w-3" />Reset to default
          </button>
        </div>
      )}
    </div>
  );
}

function FulfillmentBadge({ type }) {
  const tone = type === "WFS" ? "info" : type === "WALMART_RETAIL" ? "success" : type === "SELLER_FULFILLED" ? "neutral" : "neutral";
  return <StatusBadge tone={tone} dot={false}>{FULFILLMENT_LABEL[type]}</StatusBadge>;
}

// ---- Offers subsection ----
function OffersSubsection({ offers, period, days, selectedId, setSelectedId, onUseInCalc }) {
  const prefs = useColumnPrefs();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const priceGraphRef = useRef(null);
  const stockGraphRef = useRef(null);
  const hourly = days <= 1;

  const rows = useMemo(() => offers.filter((o) =>
    (filter === "all" || o.fulfillmentType === filter) && (o.sellerName || "").toLowerCase().includes(q.toLowerCase())), [offers, filter, q]);
  const priceMatrix = useMemo(() => buildOfferSeriesMatrix(offers, days, "landedPrice"), [offers, days]);
  const stockMatrix = useMemo(() => buildOfferSeriesMatrix(offers, days, "stock"), [offers, days]);
  // Stable, per-seller {t,v} series for in-row mini-charts (memoized; each seller uses its OWN observations).
  const seriesByOffer = useMemo(() => {
    const m = {};
    offers.forEach((o) => { m[o.offerId] = { price: buildOfferSeries(o, days, "landedPrice"), stock: buildOfferSeries(o, days, "stock") }; });
    return m;
  }, [offers, days]);
  const colorFor = (id) => SELLER_COLORS[offers.findIndex((o) => o.sellerId === id) % SELLER_COLORS.length];
  const selectSeller = (id, which) => { setSelectedId(id); (which === "stock" ? stockGraphRef : priceGraphRef).current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); };
  const legendToggle = (id) => setHidden((h) => { const n = new Set(h); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const summary = {
    count: offers.length,
    wfs: offers.filter((o) => o.isWfs).length,
    lowLanded: Math.min(...offers.map((o) => o.landedPrice).filter((x) => x != null)),
  };

  const th = "px-3 py-2 text-micro text-ink-muted text-left whitespace-nowrap border-b border-edge-subtle";

  const cell = (id, o) => {
    switch (id) {
      case "seller": return (
        <td className="px-3 py-2 border-b border-edge-subtle sticky left-0 bg-surface-0 min-w-[190px]">
          <div className="flex items-center gap-2">
            <button onClick={() => setExpanded(expanded === o.offerId ? null : o.offerId)} data-testid={`offer-expand-${o.offerId}`} className="text-ink-muted hover:text-ink-primary">
              {expanded === o.offerId ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            <div className="min-w-0">
              <div className="text-ink-primary font-medium truncate max-w-[150px]">{o.sellerName}</div>
              <div className="flex items-center gap-1.5">
                <span className="ident text-micro text-ink-muted">{o.sellerId}</span>
                <a href={o.sellerStorefrontUrl} target="_blank" rel="noreferrer" className="text-signal-blue" title="Seller storefront"><ExternalLink className="h-3 w-3" /></a>
              </div>
              <div className="mt-0.5"><FulfillmentBadge type={o.fulfillmentType} /></div>
            </div>
          </div>
        </td>);
      case "price": return (
        <td className="px-3 py-2 border-b border-edge-subtle tabnum" title={`Item ${usd(o.itemPrice)} + Shipping ${o.shippingPrice === 0 ? "Free" : usd(o.shippingPrice)}`}>
          <div className="font-semibold text-ink-primary">{o.landedPrice != null ? usd(o.landedPrice) : DASH}</div>
          <div className="text-micro text-ink-muted">{usd(o.itemPrice)} + {o.shippingPrice === 0 ? "Free" : o.shippingPrice == null ? DASH : usd(o.shippingPrice)}</div>
        </td>);
      case "condition": return <td className="px-3 py-2 border-b border-edge-subtle">{o.condition}{o.conditionComment && <Info text={o.conditionComment} />}</td>;
      case "priceHistory": return (
        <td className="px-3 py-2 border-b border-edge-subtle" style={{ minWidth: 236 }}>
          <MiniChart data={seriesByOffer[o.offerId].price} color={colorFor(o.sellerId)} unit="$" hourly={hourly} testid={`sparkline-price-${o.offerId}`} onClick={(e) => { e.stopPropagation(); selectSeller(o.sellerId, "price"); }} label={`${o.sellerName} landed price history — open full graph`} />
        </td>);
      case "stock": return <td className="px-3 py-2 border-b border-edge-subtle tabnum" title={o.lastSeenAt ? `Observed ${ago(o.lastSeenAt)}` : ""}>{o.stock != null ? <>{num(o.stock)} <span className="text-micro text-ink-muted">est.</span></> : DASH}</td>;
      case "sold": return <td className="px-3 py-2 border-b border-edge-subtle tabnum" title="Estimated units sold (lifetime observed window)">{o.sold != null ? <>{num(o.sold)} <span className="text-micro text-ink-muted">est.</span></> : DASH}</td>;
      case "sold30": return <td className="px-3 py-2 border-b border-edge-subtle tabnum" title="Estimated units sold — rolling 30 days">{o.sold30Days != null ? <>{num(o.sold30Days)} <span className="text-micro text-ink-muted">est.</span></> : DASH}</td>;
      case "stockHistory": return (
        <td className="px-3 py-2 border-b border-edge-subtle" style={{ minWidth: 236 }}>
          <MiniChart data={seriesByOffer[o.offerId].stock} color={colorFor(o.sellerId)} unit="" hourly={hourly} testid={`sparkline-stock-${o.offerId}`} onClick={(e) => { e.stopPropagation(); selectSeller(o.sellerId, "stock"); }} label={`${o.sellerName} stock history — open full graph`} />
        </td>);
      case "walmartPlus": return <td className="px-3 py-2 border-b border-edge-subtle">{o.isWalmartPlusEligible === undefined ? <span title={NP}>{DASH}</span> : <StatusBadge tone={o.isWalmartPlusEligible ? "success" : "neutral"} dot={false}>{o.isWalmartPlusEligible ? "Walmart+ Eligible" : "Not Walmart+"}</StatusBadge>}</td>;
      case "sra": return <td className="px-3 py-2 border-b border-edge-subtle text-ink-muted"><span title={NP}>{DASH}</span></td>;
      case "isWalmart": return <td className="px-3 py-2 border-b border-edge-subtle">{o.isWalmartRetail === undefined ? <span title={NP}>{DASH}</span> : bool(o.isWalmartRetail)}</td>;
      case "rating12": return <td className="px-3 py-2 border-b border-edge-subtle tabnum">{o.sellerRating12Months != null ? o.sellerRating12Months : DASH}</td>;
      case "reviewsLifetime": return <td className="px-3 py-2 border-b border-edge-subtle tabnum">{o.sellerLifetimeReviewCount != null ? num(o.sellerLifetimeReviewCount) : DASH}</td>;
      case "coupon": return <td className="px-3 py-2 border-b border-edge-subtle text-ink-muted"><span title={NP}>{DASH}</span></td>;
      case "current": return <td className="px-3 py-2 border-b border-edge-subtle">{o.isOutOfStock ? <StatusBadge tone="danger" dot={false}>Out of stock</StatusBadge> : o.isSuppressed ? <StatusBadge tone="neutral" dot={false}>Suppressed</StatusBadge> : o.isCurrent ? <StatusBadge tone="success" dot={false}>Current</StatusBadge> : <StatusBadge tone="neutral" dot={false}>Historical</StatusBadge>}</td>;
      case "firstSeen": return <td className="px-3 py-2 border-b border-edge-subtle text-micro text-ink-muted whitespace-nowrap" title={o.firstSeenAt}>{fmtD(o.firstSeenAt)}</td>;
      default: return <td className="px-3 py-2 border-b border-edge-subtle">{DASH}</td>;
    }
  };

  const sellersMeta = offers.map((o) => ({ sellerId: o.sellerId, sellerName: o.sellerName }));

  return (
    <div className="space-y-4" data-testid="offers-subsection">
      {/* summary */}
      <Panel className="px-1 py-1" testid="offers-summary">
        <div className="flex flex-wrap items-stretch divide-x divide-edge-subtle">
          {[["Sellers", num(summary.count)], ["WFS offers", num(summary.wfs)], ["Lowest landed", usd(summary.lowLanded)], ["Period", period]].map(([l, v]) => (
            <div key={l} className="px-4 py-2 min-w-[110px]"><div className="text-micro uppercase text-ink-muted font-mono">{l}</div><div className="text-metricsm font-mono tabnum mt-0.5 text-ink-primary">{v}</div></div>
          ))}
        </div>
      </Panel>

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 h-8 px-2 rounded-ctrl border border-edge-subtle bg-surface-0">
          <Search className="h-3.5 w-3.5 text-ink-muted" />
          <input data-testid="offers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sellers" className="bg-transparent text-compact focus:outline-none w-36" />
        </div>
        <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro">
          {[["all", "All"], ["WFS", "WFS"], ["SELLER_FULFILLED", "Seller fulfilled"], ["WALMART_RETAIL", "Walmart"]].map(([k, l]) => (
            <button key={k} data-testid={`offers-filter-${k}`} onClick={() => setFilter(k)} className={cn("px-2.5 h-8", filter === k ? "ez-seg-active" : "ez-seg")}>{l}</button>
          ))}
        </div>
        {selectedId && <button data-testid="offers-show-all" onClick={() => setSelectedId(null)} className="h-8 px-2.5 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">Show all sellers</button>}
        <div className="ml-auto"><ColumnMenu prefs={prefs} /></div>
      </div>

      {/* table */}
      <Panel className="p-0 overflow-hidden" testid="offers-table-panel">
        {rows.length === 0 ? (
          <EmptyState testid="offers-empty" icon={Store} title="No offers match" hint="Adjust your search or fulfillment filter." />
        ) : (
          <div className="overflow-x-auto ez-scroll">
            <table className="w-full text-compact" data-testid="offers-table">
              <thead><tr className="bg-surface-2">
                {prefs.visible.map((id) => {
                  const c = OFFER_COLUMNS.find((x) => x.id === id);
                  return <th key={id} className={cn(th, id === "seller" && "sticky left-0 bg-surface-2")}>{c.label}{c.tooltip && <Info text={c.tooltip} />}</th>;
                })}
              </tr></thead>
              <tbody>
                {rows.map((o) => (
                  <React.Fragment key={o.offerId}>
                    <tr data-testid={`offer-row-${o.offerId}`} onClick={() => setSelectedId(o.sellerId)}
                      className={cn("hover:bg-surface-1 cursor-pointer", selectedId === o.sellerId && "bg-emerald-50/60")}>
                      {prefs.visible.map((id) => <React.Fragment key={id}>{cell(id, o)}</React.Fragment>)}
                    </tr>
                    {expanded === o.offerId && (
                      <tr data-testid={`offer-expanded-${o.offerId}`} className="bg-surface-1/70">
                        <td colSpan={prefs.visible.length} className="px-4 py-3">
                          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1.5 text-compact">
                            {[
                              ["Seller ID", o.sellerId], ["Fulfillment", FULFILLMENT_LABEL[o.fulfillmentType]],
                              ["Item price", usd(o.itemPrice)], ["Shipping", o.shippingPrice === 0 ? "Free" : o.shippingPrice == null ? DASH : usd(o.shippingPrice)],
                              ["Landed price", o.landedPrice != null ? usd(o.landedPrice) : DASH], ["Walmart+ eligible", o.isWalmartPlusEligible === undefined ? DASH : bool(o.isWalmartPlusEligible)],
                              ["Free shipping", o.hasFreeShipping === undefined ? DASH : bool(o.hasFreeShipping)], ["TwoDay Delivery", o.hasTwoDayDelivery === undefined ? DASH : bool(o.hasTwoDayDelivery)],
                              ["Rating (12 mo)", o.sellerRating12Months ?? DASH], ["Lifetime reviews", o.sellerLifetimeReviewCount != null ? num(o.sellerLifetimeReviewCount) : DASH],
                              ["Condition", o.condition], ["Coupon", DASH],
                              ["Stock (est.)", o.stock != null ? num(o.stock) : DASH], ["Sold 30d (est.)", o.sold30Days != null ? num(o.sold30Days) : DASH],
                              ["First seen", fmtD(o.firstSeenAt)], ["Last seen", o.lastSeenAt ? fmtD(o.lastSeenAt) : DASH],
                              ["Last checked", o.lastCheckedAt ? ago(o.lastCheckedAt) : DASH], ["Buy Box", bool(o.isBuyBox, "Winning", "No")],
                            ].map(([k, v]) => (
                              <div key={k} className="flex justify-between gap-3 border-b border-edge-subtle/60 py-0.5"><span className="text-ink-muted">{k}</span><span className="tabnum text-ink-primary">{v}</span></div>
                            ))}
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <a href={o.sellerStorefrontUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-micro text-signal-blue hover:underline">Seller storefront<ExternalLink className="h-3 w-3" /></a>
                            <button onClick={(e) => { e.stopPropagation(); onUseInCalc(o.landedPrice); }} className="inline-flex items-center gap-1 text-micro text-emerald-600 hover:underline">Use landed in calculator<ArrowRight className="h-3 w-3" /></button>
                            <span className="ml-auto text-micro text-ink-muted"><ProvenanceTag kind="fixture" /> · seller history is estimated</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-3 py-2 border-t border-edge-subtle text-micro font-mono text-ink-muted">{DASH} = not provided by the current Walmart data source. Seller price/stock history is fixture-derived and estimated.</div>
      </Panel>

      {/* full graphs — Price and Stock kept separate */}
      <div className="grid gap-4">
        <div ref={priceGraphRef}>
          <FullGraph title="Price History (landed, per seller)" testid="offers-price-graph" points={priceMatrix} sellers={sellersMeta} selectedId={selectedId} unit="$" hidden={hidden} onLegendClick={legendToggle} hourly={hourly} />
        </div>
        <div ref={stockGraphRef}>
          <FullGraph title="Stock History (per seller)" testid="offers-stock-graph" points={stockMatrix} sellers={sellersMeta} selectedId={selectedId} unit="" hidden={hidden} onLegendClick={legendToggle} hourly={hourly} />
        </div>
      </div>
    </div>
  );
}

// ---- Buy Box Statistics subsection ----
const OWN_SEGS = (t) => [
  { k: "Walmart", v: t.walmart, c: "#F97316" }, { k: "WFS", v: t.wfs, c: "#2563EB" },
  { k: "Seller fulfilled", v: t.seller, c: "#8B5CF6" }, { k: "Suppressed / unowned", v: t.suppressed, c: "#94A3B8" },
];
function BuyBoxSubsection({ offersRaw, period, days, selectedId, setSelectedId }) {
  const stats = useMemo(() => buildBuyBoxStats(offersRaw, period, days), [offersRaw, period, days]);
  if (!stats) return <EmptyState testid="buybox-empty" icon={ShoppingBag} title="No Buy Box data" hint="No current offers to derive Buy Box statistics." />;
  const own = stats.ownership;
  const STAT = [
    ["Current Buy Box seller", stats.currentSeller], ["Current Buy Box price", usd(stats.currentPrice)],
    ["Item price", usd(stats.itemPrice)], ["Shipping", stats.shippingPrice === 0 ? "Free" : usd(stats.shippingPrice)],
    ["Total landed", usd(stats.landedPrice)], ["Fulfillment", stats.fulfillment],
    ["Current seller rating", stats.currentRating ?? DASH], ["Avg winning price", usd(stats.avgWinningPrice)],
    ["Min winning price", usd(stats.minWinningPrice)], ["Max winning price", usd(stats.maxWinningPrice)],
    ["Win count", stats.winCount ?? DASH], ["Rotations", stats.rotationCount ?? DASH],
    ["Current streak", stats.currentStreak], ["First win", fmtD(stats.firstWin)], ["Last win", ago(stats.lastWin)],
  ];
  return (
    <div className="space-y-4" data-testid="buybox-subsection">
      <Panel className="p-4" testid="buybox-cards">
        <div className="flex items-center justify-between mb-3"><span className="text-stitle text-ink-primary">Buy Box (Featured Offer) — current & winning statistics</span><StatusBadge tone="warning">New condition</StatusBadge></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-2 text-compact">
          {STAT.map(([k, v]) => (<div key={k} className="min-w-0"><div className="text-micro text-ink-muted">{k}</div><div className="tabnum font-medium text-ink-primary truncate">{v}</div></div>))}
        </div>
      </Panel>

      <Panel className="p-4" testid="buybox-price-history">
        <div className="text-stitle text-ink-primary mb-2">Buy Box price history</div>
        <div style={{ width: "100%", height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats.history} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" vertical={false} />
              <XAxis dataKey="t" tickFormatter={fmtD} minTickGap={48} tick={{ fontSize: 10, fill: "#8A94A6" }} />
              <YAxis width={44} tick={{ fontSize: 10, fill: "#8A94A6" }} tickFormatter={(v) => `$${v}`} />
              <RTooltip formatter={(v) => usd(v)} labelFormatter={fmtDT} />
              <Line dataKey="price" stroke="#059669" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <Panel className="p-0 overflow-hidden" testid="buybox-ownership">
        <div className="px-4 py-2.5 border-b border-edge-subtle flex items-center justify-between">
          <span className="text-stitle text-ink-primary">Buy Box Ownership by Seller ({own.period})</span><ProvenanceTag kind="estimated" />
        </div>
        <div className="px-4 pt-3">
          <div className="flex h-2.5 rounded-full overflow-hidden bg-surface-2" role="img" aria-label="Buy Box ownership by fulfillment">
            {OWN_SEGS(own.totals).map((s) => (s.v > 0 ? <span key={s.k} style={{ width: `${s.v}%`, background: s.c }} title={`${s.k} ${s.v}%`} /> : null))}
          </div>
        </div>
        <div className="overflow-x-auto ez-scroll">
          <table className="w-full text-compact" style={{ minWidth: 860 }} data-testid="buybox-ownership-table">
            <thead><tr className="text-micro text-ink-muted text-left bg-surface-2">
              {["Seller", "Current Buy Box", "Fulfillment", "Ownership share", "Ownership time", "Rotations", "Avg landed", "Current landed", "Streak", "Last observed"].map((h) => <th key={h} className="px-3 py-2 border-b border-edge-subtle whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {own.sellers.map((s, i) => (
                <tr key={i} onClick={() => setSelectedId(null)} className={cn("hover:bg-surface-1 cursor-pointer", s.featured && "bg-emerald-100/40")}>
                  <td className="px-3 py-2 border-b border-edge-subtle text-ink-primary whitespace-nowrap">{s.seller}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle">{s.featured ? <StatusBadge tone="success">Winning</StatusBadge> : <span className="text-ink-muted">Not winning</span>}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle text-ink-secondary whitespace-nowrap">{s.fulfillmentLabel}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle tabnum">{s.share}%</td>
                  <td className="px-3 py-2 border-b border-edge-subtle tabnum">{s.ownershipDays}d</td>
                  <td className="px-3 py-2 border-b border-edge-subtle tabnum">{s.rotation}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle tabnum">{usd(s.avgLanded)}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle tabnum">{usd(s.currentLanded)}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle tabnum">{s.streak}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle text-micro text-ink-muted whitespace-nowrap">{ago(s.lastObserved)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

// ---- Used Buy Box Statistics subsection ----
function UsedBuyBoxSubsection({ offersRaw }) {
  const used = useMemo(() => buildUsedBuyBoxStats(offersRaw), [offersRaw]);
  const FIELDS = ["Current Used Buy Box seller", "Current Used Buy Box price", "Condition / subcondition", "Item price", "Shipping", "Total landed", "Used ownership %", "Used win count", "Rotations", "Avg used winning price", "Min used winning price", "Max used winning price", "First used win", "Last used win"];
  return (
    <div className="space-y-4" data-testid="used-buybox-subsection">
      <Panel className="p-4">
        <div className="flex items-center gap-2 mb-3"><span className="text-stitle text-ink-primary">Used Buy Box statistics</span><StatusBadge tone="neutral">Used condition</StatusBadge></div>
        {used.available ? (
          <div className="text-compact text-ink-secondary">Used-condition offers detected — rendering used Buy Box statistics.</div>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded-ctrl border border-amber-200 bg-amber-50/60 p-3 mb-3" data-testid="used-buybox-unavailable">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-compact text-ink-secondary">{used.reason} The current Walmart source returns only New-condition observations, so used-condition Buy Box values are shown as {DASH}. New-condition values are never substituted here.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-2 text-compact">
              {FIELDS.map((k) => (<div key={k} className="min-w-0"><div className="text-micro text-ink-muted">{k}</div><div className="tabnum text-ink-muted" title={used.reason}>{DASH}</div></div>))}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

export function WalmartOffersWorkspace({
  bundle,
  section,
  period,
  days,
  selectedId,
  setSelectedId,
  onUseInCalc,
  liveMode = false,
}) {
  const offersRaw = bundle?.offers || [];
  const offers = useMemo(() => adaptOffers(offersRaw), [offersRaw]);
  const unavailable =
    liveMode ||
    bundle?.available === false ||
    bundle?.offers_reason === "offers_unavailable" ||
    (Array.isArray(bundle?.offers) && bundle.offers.length === 0 && bundle?.available === false);

  if (unavailable || (Array.isArray(bundle?.offers) && !offersRaw.length && bundle != null && (liveMode || bundle.available === false))) {
    if (section === "buybox" || section === "used-buybox" || section === "offers" || !section) {
      return (
        <div className="p-10 text-center" data-testid="offers-unavailable">
          <div className="text-compact font-medium text-ink-primary">Seller offers not available</div>
          <p className="text-compact text-ink-secondary mt-1 max-w-md mx-auto">
            {bundle?.message ||
              "Walmart Marketplace search does not return a live seller list for this product."}
          </p>
          <p className="text-micro font-mono text-ink-muted mt-2" data-testid="offers-unavailable-reason">
            {bundle?.offers_reason || bundle?.reasons?.offers || "offers_unavailable"}
          </p>
        </div>
      );
    }
  }

  if (!bundle) {
    return <div className="p-10 text-center text-compact text-ink-muted" data-testid="offers-loading">Loading offers…</div>;
  }
  if (!offersRaw.length) {
    return (
      <div className="p-10 text-center" data-testid="offers-empty">
        <div className="text-compact font-medium text-ink-primary">No marketplace offers</div>
        <p className="text-compact text-ink-secondary mt-1">No seller offers are available for this product.</p>
      </div>
    );
  }
  if (section === "buybox") return <BuyBoxSubsection offersRaw={offersRaw} period={period} days={days} selectedId={selectedId} setSelectedId={setSelectedId} />;
  if (section === "used-buybox") return <UsedBuyBoxSubsection offersRaw={offersRaw} />;
  return <OffersSubsection offers={offers} period={period} days={days} selectedId={selectedId} setSelectedId={setSelectedId} onUseInCalc={onUseInCalc} />;
}

export default WalmartOffersWorkspace;
